/**
 * Municipalities Service
 * Handles all API operations for municipalities with AbortController support
 */

import { get, post, put, del, patch, apiClient } from '@/lib/api-client';
import { db } from '@/lib/db';
import { cacheMunicipality, getCachedMunicipality } from '@/lib/db';
import type {
  Municipality,
  MunicipalitySummary,
} from '@/types/domain.types';
import type { GeoPolygon, GeoPoint } from '@/lib/geo-utils';

export interface CreateMunicipalityRequest {
  name: string;
  province: string;
  displayName?: string;
  type?: 'city' | 'county' | 'town' | 'village' | 'municipality';
  center?: GeoPoint;
  boundary?: GeoPolygon | null;
  boundingBox?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

export interface UpdateMunicipalityRequest {
  name?: string;
  province?: string;
}

export interface MunicipalityStats {
  propertiesCount: number;
  pendingSubmissionsCount: number;
  reviewedTodayCount: number;
  rejectedTodayCount: number;
  lastActivityAt?: string;
}

/**
 * A single map-data ingest-status doc served by
 * `GET /municipalities/{id}/ingest-status` — one row per (municipality,
 * layer) pair, upserted by the FME/bucket ingest pipeline. Field names arrive
 * snake_case from Firestore and are camelCased by the api-client interceptor.
 *
 * `status` moves through staged values on the ortho path
 * (validating → tiling → packing → uploading → ready) and lands on a terminal
 * value on the vector path (ready / failed / skipped_older / too_large /
 * skipped_invalid_name / skipped_over_cap). `skipped_unknown` is a legacy
 * terminal value from before the process-and-hold-layers design
 * (2026-07-21) — every valid, in-cap table is processed regardless of the
 * muni's allow-list now, so no NEW row is ever written with it, but old rows
 * can still carry it until their next re-drop. The string union is left
 * open to tolerate future/unknown values.
 */
export type IngestLayerStatus =
  | 'validating'
  | 'tiling'
  | 'packing'
  | 'uploading'
  | 'processing'
  | 'ready'
  | 'failed'
  | 'skipped_older'
  | 'skipped_unknown'
  | 'skipped_invalid_name'
  | 'skipped_over_cap'
  | 'too_large'
  | (string & {});

export interface IngestStatusDoc {
  municipalityId: string;
  layerKey: string;
  status: IngestLayerStatus;
  /** ISO-8601 timestamp of the most recent write, or null if never set. */
  lastReceivedAt?: string | null;
  /** GCS source object path the last write was triggered by. */
  lastObject?: string | null;
  featureCount?: number | null;
  skippedCount?: number | null;
  /** Populated (and shown) when `status === 'failed'`. */
  error?: string | null;
  /** Full refusal list behind the `error` headline for a `failed` CAMA
   *  drop (2026-09-03). Server-capped at 50 entries × 500 chars. */
  errorDetails?: string[] | null;
  /** True number of refusal reasons — may exceed `errorDetails.length`. */
  errorCount?: number | null;
  /** Human-readable progress/reason string — a `waiting` CAMA drop's
   *  "waiting on session … to finish", an ortho stage's "zoom 14/18", or
   *  (R1/I1, 2026-09-03) for a `failed` CAMA drop written by the
   *  execution-failure or quarantine producer, that producer's own
   *  recovery instruction — rendered by the CAMA-tab banner in place of
   *  its generic "fix the export and push again" default. */
  detail?: string | null;
  sourceGeneration?: number | null;
}

/**
 * FME drop-contract info for the Map Data page, served by
 * `GET /municipalities/{id}/ingest-info` — a read-only view of the
 * operator-seeded ingest binding on the municipality doc. `provisioned`
 * is true iff both `ingestKey` and `bucket` are present; when false the
 * page renders the un-provisioned panel and ignores the other fields.
 */
export interface IngestInfo {
  provisioned: boolean;
  ingestKey: string | null;
  bucket: string | null;
  expectedEpsg: number | null;
  displayLayers: string[];
  /**
   * Operator-seeded switch: whether this municipality's binding expects a
   * `cama.sqlite` drop at all. `false` for the vast majority of
   * municipalities (CAMA-via-FME is opt-in) — `CamaDropInstructions`
   * renders nothing in that case, same as an un-provisioned binding.
   *
   * Required: the backend (routes/map_layers.py::get_ingest_info) always
   * sends this field.
   */
  camaExpected: boolean;
}

const CACHED_SUMMARIES_KEY = 'cachedMunicipalitySummaries';

async function cacheMunicipalitySummaries(summaries: MunicipalitySummary[]): Promise<void> {
  try {
    await db.settings.put({
      key: CACHED_SUMMARIES_KEY,
      value: { summaries, cachedAt: Date.now() } as Record<string, unknown>,
    });
  } catch (err) {
    console.warn('Failed to cache municipality summaries:', err);
  }
}

async function getCachedMunicipalitySummaries(): Promise<MunicipalitySummary[] | null> {
  try {
    const setting = await db.settings.get(CACHED_SUMMARIES_KEY);
    if (!setting || typeof setting.value !== 'object' || setting.value === null) {
      return null;
    }
    const cached = setting.value as Record<string, unknown>;
    if (!Array.isArray(cached.summaries)) {
      return null;
    }
    return cached.summaries as MunicipalitySummary[];
  } catch (err) {
    console.warn('Failed to read cached municipality summaries:', err);
    return null;
  }
}

/**
 * Municipalities service with AbortController support for all async operations
 */
export const municipalitiesService = {
  /**
   * Get all municipalities for an organization
   */
  getByOrganization: async (
    organizationId: string,
    options?: { signal?: AbortSignal }
  ): Promise<Municipality[]> => {
    return get<Municipality[]>(
      `/organizations/${organizationId}/municipalities`,
      { signal: options?.signal }
    );
  },

  /**
   * Get municipalities summary for dashboard
   * Caches result for offline use, falls back to cache if API fails
   */
  getSummaries: async (
    organizationId: string,
    options?: { signal?: AbortSignal }
  ): Promise<MunicipalitySummary[]> => {
    try {
      const data = await get<MunicipalitySummary[]>(
        `/organizations/${organizationId}/municipalities/summary`,
        { signal: options?.signal }
      );
      // Cache for offline use
      await cacheMunicipalitySummaries(data);
      return data;
    } catch (err) {
      // Try cache on API failure (offline mode)
      const cached = await getCachedMunicipalitySummaries();
      if (cached) {
        return cached;
      }
      throw err;
    }
  },

  /**
   * Get a single municipality by ID
   * Caches result for offline use, falls back to cache if API fails
   */
  getById: async (
    municipalityId: string,
    options?: { signal?: AbortSignal }
  ): Promise<Municipality> => {
    try {
      const data = await get<Municipality>(
        `/municipalities/${municipalityId}`,
        { signal: options?.signal }
      );
      // Cache for offline use
      await cacheMunicipality(data.id, data.name).catch(() => {
        // Ignore cache errors - non-critical
      });
      return data;
    } catch (err) {
      // Try cache on API failure (offline mode)
      const cached = await getCachedMunicipality(municipalityId).catch(() => null);
      if (cached) {
        return {
          id: cached.id,
          name: cached.name,
          province: '',
          displayName: cached.name,
          organizationId: '',
          createdAt: '',
          updatedAt: '',
        } as Municipality;
      }
      throw err;
    }
  },

  /**
   * Get municipality statistics
   */
  getStats: async (
    municipalityId: string,
    options?: { signal?: AbortSignal }
  ): Promise<MunicipalityStats> => {
    return get<MunicipalityStats>(
      `/municipalities/${municipalityId}/stats`,
      { signal: options?.signal }
    );
  },

  /**
   * Create a new municipality
   */
  create: async (
    organizationId: string,
    data: CreateMunicipalityRequest,
    options?: { signal?: AbortSignal }
  ): Promise<Municipality> => {
    return post<Municipality>(
      `/organizations/${organizationId}/municipalities`,
      data,
      { signal: options?.signal }
    );
  },

  /**
   * Update a municipality
   */
  update: async (
    municipalityId: string,
    data: UpdateMunicipalityRequest,
    options?: { signal?: AbortSignal }
  ): Promise<Municipality> => {
    return put<Municipality>(
      `/municipalities/${municipalityId}`,
      data,
      { signal: options?.signal }
    );
  },

  /**
   * Delete a municipality
   */
  delete: async (
    municipalityId: string,
    options?: { signal?: AbortSignal }
  ): Promise<void> => {
    return del<void>(
      `/municipalities/${municipalityId}`,
      { signal: options?.signal }
    );
  },

  /**
   * Search municipalities by name
   */
  search: async (
    organizationId: string,
    query: string,
    options?: { signal?: AbortSignal }
  ): Promise<Municipality[]> => {
    return get<Municipality[]>(
      `/organizations/${organizationId}/municipalities/search`,
      {
        params: { q: query },
        signal: options?.signal,
      }
    );
  },

  /**
   * Get list of Canadian provinces for dropdown
   */
  getProvinces: (): Array<{ code: string; name: string }> => {
    return [
      { code: 'AB', name: 'Alberta' },
      { code: 'BC', name: 'British Columbia' },
      { code: 'MB', name: 'Manitoba' },
      { code: 'NB', name: 'New Brunswick' },
      { code: 'NL', name: 'Newfoundland and Labrador' },
      { code: 'NS', name: 'Nova Scotia' },
      { code: 'NT', name: 'Northwest Territories' },
      { code: 'NU', name: 'Nunavut' },
      { code: 'ON', name: 'Ontario' },
      { code: 'PE', name: 'Prince Edward Island' },
      { code: 'QC', name: 'Quebec' },
      { code: 'SK', name: 'Saskatchewan' },
      { code: 'YT', name: 'Yukon' },
    ];
  },
  /**
   * List map-data ingest statuses for a municipality (read-only).
   *
   * Returns one row per (municipality, layer) pair as written by the
   * FME/bucket ingest pipeline. Municipality-scoped only — these docs carry
   * no cycle dimension (map data is uploaded once per muni and persists
   * across cycles).
   */
  getIngestStatus: async (
    municipalityId: string,
    options?: { signal?: AbortSignal }
  ): Promise<IngestStatusDoc[]> => {
    return get<IngestStatusDoc[]>(
      `/municipalities/${municipalityId}/ingest-status`,
      { signal: options?.signal }
    );
  },

  /**
   * FME drop-contract info for the Map Data page (read-only).
   */
  getIngestInfo: async (
    municipalityId: string,
    options?: { signal?: AbortSignal }
  ): Promise<IngestInfo> => {
    return get<IngestInfo>(
      `/municipalities/${municipalityId}/ingest-info`,
      { signal: options?.signal }
    );
  },

  /**
   * Replace the municipality's FME display-layers allow-list. Curation is
   * serve-time only now (process-and-hold-layers design, 2026-07-21 spec
   * §4/§6): Allow makes a held layer instantly servable, Remove just hides
   * it (computed `allowed` flips false on the next GET /map-layers) — this
   * PATCH no longer sweeps/deprecates any registry doc, so the response is
   * the same fresh ingest info GET /ingest-info returns.
   */
  async updateDisplayLayers(
    municipalityId: string,
    displayLayers: string[]
  ): Promise<IngestInfo> {
    return patch<IngestInfo>(
      `/municipalities/${municipalityId}/ingest-info`,
      { displayLayers }
    );
  },

  /**
   * Toggle feature flags for a municipality.
   */
  updateFeatures: async (
    municipalityId: string,
    features: { rfiBuilder?: boolean },
    options?: { signal?: AbortSignal }
  ): Promise<{ features: Record<string, boolean>; message: string }> => {
    const response = await apiClient.patch(
      `/municipalities/${municipalityId}/features`,
      features,
      { signal: options?.signal }
    );
    return response.data;
  },
};

export default municipalitiesService;
